import { Empty, Input, Modal, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigationAids } from '../../../app/providers/NavigationAidsProvider';
import { useGuardedNavigate } from '../../../app/providers/FormSafetyProvider';
import styles from './CommandPalette.module.css';

const { Text } = Typography;

function matchesQuery(item, query) {
  if (!query) {
    return true;
  }

  const haystack = [
    item.label,
    item.hint,
    item.category,
    ...(item.keywords || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return query
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
}

export default function CommandPalette() {
  const guardedNavigate = useGuardedNavigate();
  const { commandItems, commandPaletteOpen, closeCommandPalette } = useNavigationAids();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();
  const resultItemRefs = useRef(new Map());
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());

  useEffect(() => {
    if (!commandPaletteOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [commandPaletteOpen]);

  const visibleItems = useMemo(
    () => commandItems.filter((item) => matchesQuery(item, deferredQuery)).slice(0, 10),
    [commandItems, deferredQuery]
  );

  useEffect(() => {
    if (!visibleItems.length) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex((currentValue) =>
      currentValue >= visibleItems.length ? visibleItems.length - 1 : currentValue
    );
  }, [visibleItems]);

  useEffect(() => {
    const activeItem = visibleItems[activeIndex];
    if (!activeItem) {
      return;
    }

    const node = resultItemRefs.current.get(activeItem.id);
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, visibleItems]);

  const handleSelect = (path) => {
    guardedNavigate(path);
    closeCommandPalette();
  };

  const handleSearchKeyDown = (event) => {
    if (!visibleItems.length) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCommandPalette();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((currentValue) => (currentValue + 1) % visibleItems.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((currentValue) =>
        (currentValue - 1 + visibleItems.length) % visibleItems.length
      );
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(visibleItems[activeIndex]?.path || visibleItems[0].path);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeCommandPalette();
    }
  };

  const activeOptionId = visibleItems[activeIndex]
    ? `${listboxId}-option-${visibleItems[activeIndex].id}`
    : undefined;

  return (
    <Modal
      open={commandPaletteOpen}
      footer={null}
      onCancel={closeCommandPalette}
      width={680}
      closable={false}
      centered
      className={styles.modal}
      destroyOnHidden
      focusTriggerAfterClose={false}
    >
      <div className={styles.shell} data-testid="command-palette">
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Command palette</h2>
            <p className={styles.summary}>
              Jump to pages, recent views, favorites, and common create actions.
            </p>
          </div>
          <span className={styles.shortcutPill}>Ctrl/Cmd + K</span>
        </div>

        <Input
          autoFocus
          size="large"
          className={styles.search}
          prefix={<SearchOutlined />}
          placeholder="Search pages, actions, and favorites"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          role="combobox"
          aria-expanded={commandPaletteOpen}
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
        />

        <div
          id={listboxId}
          className={styles.resultList}
          role="listbox"
          aria-label="Command palette results"
        >
          {visibleItems.length ? (
            visibleItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeIndex === index;
              const optionId = `${listboxId}-option-${item.id}`;

              return (
                <button
                  key={item.id}
                  id={optionId}
                  ref={(node) => {
                    if (node) {
                      resultItemRefs.current.set(item.id, node);
                      return;
                    }

                    resultItemRefs.current.delete(item.id);
                  }}
                  type="button"
                  className={`${styles.resultItem} ${isActive ? styles.resultItemActive : ''}`}
                  data-active={isActive ? 'true' : 'false'}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSelect(item.path)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className={styles.resultIcon}>
                    {Icon ? <Icon /> : <SearchOutlined />}
                  </span>
                  <span className={styles.resultCopy}>
                    <span className={styles.resultLabel}>{item.label}</span>
                    <span className={styles.resultHint}>{item.hint}</span>
                  </span>
                  <Text className={styles.resultMeta}>{item.category}</Text>
                </button>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No matching navigation results."
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
